// frontend/src/pages/superadmin/PackagesManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Package, Plus, Edit2, Trash2, X, CheckCircle, Save, DollarSign, Calendar
} from 'lucide-react';
import { getPackages, createPackage, updatePackage, deletePackage } from '../../services/superAdminApi';

const PackagesManager = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    sort_order: 0,
    is_active: true,
    features: {}
  });

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const response = await getPackages();
      setPackages(response.data || []);
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (editingPackage) {
        await updatePackage(editingPackage.id, formData);
      } else {
        await createPackage(formData);
      }
      setShowModal(false);
      resetForm();
      await loadPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Error saving package');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (pkg) => {
    if (window.confirm(`Delete package "${pkg.display_name}"?`)) {
      try {
        await deletePackage(pkg.id);
        await loadPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
        alert('Error deleting package');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      price_monthly: 0,
      price_yearly: 0,
      sort_order: 0,
      is_active: true,
      features: {}
    });
    setEditingPackage(null);
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name || '',
      display_name: pkg.display_name || '',
      description: pkg.description || '',
      price_monthly: pkg.price_monthly || 0,
      price_yearly: pkg.price_yearly || 0,
      sort_order: pkg.sort_order || 0,
      is_active: pkg.is_active !== false,
      features: pkg.features || {}
    });
    setShowModal(true);
  };

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature]
      }
    }));
  };

  const availableFeatures = [
    'fuel_management', 'maintenance_tracking', 'flight_scheduling',
    'invoicing', 'staff_management', 'owner_portal', 'reporting',
    'api_access', 'mobile_app', 'analytics', 'chat_support', 'video_surveillance'
  ];

  const featureLabels = {
    fuel_management: '⛽ Fuel Management',
    maintenance_tracking: '🔧 Maintenance Tracking',
    flight_scheduling: '✈️ Flight Scheduling',
    invoicing: '💰 Invoicing',
    staff_management: '👥 Staff Management',
    owner_portal: '🏠 Owner Portal',
    reporting: '📊 Reporting',
    api_access: '🔌 API Access',
    mobile_app: '📱 Mobile App',
    analytics: '📈 Analytics',
    chat_support: '💬 Chat Support',
    video_surveillance: '🎥 Video Surveillance'
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Packages Management</h1>
          <p className="text-gray-400 text-sm">Manage subscription packages for airports</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Package
        </button>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) => (
          <div key={pkg.id} className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-white font-semibold text-lg">{pkg.display_name}</h3>
                  <p className="text-gray-400 text-sm">{pkg.name}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(pkg)}
                    className="p-1.5 hover:bg-gray-700 rounded-lg text-blue-400"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(pkg)}
                    className="p-1.5 hover:bg-gray-700 rounded-lg text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-gray-300 text-sm">{pkg.description}</p>
              <div className="flex gap-4">
                <div className="flex items-center gap-1 text-green-400">
                  <DollarSign className="w-4 h-4" />
                  <span className="font-medium">{pkg.price_monthly}/month</span>
                </div>
                <div className="flex items-center gap-1 text-blue-400">
                  <DollarSign className="w-4 h-4" />
                  <span className="font-medium">{pkg.price_yearly}/year</span>
                </div>
              </div>
              {pkg.features && Object.keys(pkg.features).length > 0 && (
                <div className="flex flex-wrap gap-1 pt-2">
                  {Object.entries(pkg.features).filter(([_, active]) => active).map(([key]) => (
                    <span key={key} className="text-xs px-2 py-0.5 bg-gray-700 rounded-full text-gray-300">
                      {featureLabels[key] || key}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex justify-between items-center pt-2 text-xs">
                <span className="text-gray-500">Order: {pkg.sort_order}</span>
                {pkg.is_active ? (
                  <span className="flex items-center gap-1 text-green-400">
                    <CheckCircle className="w-3 h-3" /> Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-red-400">
                    <X className="w-3 h-3" /> Inactive
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Création/Édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">
                {editingPackage ? 'Edit Package' : 'New Package'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Name (slug) *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    placeholder="basic_premium"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Display Name *</label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    placeholder="Basic Premium"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Monthly Price ($)</label>
                  <input
                    type="number"
                    value={formData.price_monthly}
                    onChange={(e) => setFormData({ ...formData, price_monthly: parseFloat(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Yearly Price ($)</label>
                  <input
                    type="number"
                    value={formData.price_yearly}
                    onChange={(e) => setFormData({ ...formData, price_yearly: parseFloat(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Status</label>
                  <select
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Features</label>
                <div className="grid grid-cols-2 gap-2">
                  {availableFeatures.map((feature) => (
                    <label key={feature} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.features[feature] || false}
                        onChange={() => toggleFeature(feature)}
                        className="w-4 h-4 rounded border-gray-600"
                      />
                      <span className="text-gray-300 text-sm">{featureLabels[feature]}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {submitting ? 'Saving...' : (editingPackage ? 'Update' : 'Create')}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackagesManager;